// Extrait de DataTab.js (handover bloc F1) — JSX, textes et appels API inchangés.
import { useState, useEffect } from "react";
import api from "@/api";
import { toast } from "sonner";
import { Database, FileText, Download, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";
import { AlertBadge, formatScore } from "./shared";

export default function ClinicalView({ filters }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.clinicId) params.append("clinic_id", filters.clinicId);
      if (filters.startDate) params.append("start_date", filters.startDate);
      if (filters.endDate) params.append("end_date", filters.endDate);
      if (filters.alertLevel) params.append("alert_level", filters.alertLevel);
      if (filters.protocol) params.append("protocol", filters.protocol);
      params.append("page", page);
      params.append("page_size", 50);
      
      const res = await api.get(`/data/clinical?${params.toString()}`);
      setData(res.data);
    } catch (err) {
      toast.error("Erreur lors du chargement des données cliniques");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filters, page]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.clinicId) params.append("clinic_id", filters.clinicId);
      if (filters.startDate) params.append("start_date", filters.startDate);
      if (filters.endDate) params.append("end_date", filters.endDate);
      
      const res = await api.get(`/data/export/clinical?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `clinical_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Export téléchargé");
    } catch (err) {
      toast.error("Erreur lors de l'export");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <FileText className="w-4 h-4 text-emerald-600" />
            Données Cliniques
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            Historique des check-ins patients avec symptômes, constantes et alertes
          </p>
        </div>
        <button
          onClick={handleExport}
          disabled={exporting || !data?.items?.length}
          data-testid="export-clinical-btn"
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download className="w-4 h-4" />
          {exporting ? "Export..." : "Export CSV"}
        </button>
      </div>

      {/* Data Table */}
      <div className="dash-card overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left p-3 font-medium text-slate-600 whitespace-nowrap">Date</th>
                <th className="text-left p-3 font-medium text-slate-600 whitespace-nowrap">Patient</th>
                <th className="text-left p-3 font-medium text-slate-600 whitespace-nowrap">Clinique</th>
                <th className="text-left p-3 font-medium text-slate-600 whitespace-nowrap">Cancer</th>
                <th className="text-left p-3 font-medium text-slate-600 whitespace-nowrap">Traitement</th>
                <th className="text-left p-3 font-medium text-slate-600 whitespace-nowrap">Fatigue</th>
                <th className="text-left p-3 font-medium text-slate-600 whitespace-nowrap">Douleur</th>
                <th className="text-left p-3 font-medium text-slate-600 whitespace-nowrap">Nausées</th>
                <th className="text-left p-3 font-medium text-slate-600 whitespace-nowrap">T°C</th>
                <th className="text-left p-3 font-medium text-slate-600 whitespace-nowrap">TA</th>
                <th className="text-left p-3 font-medium text-slate-600 whitespace-nowrap">Alerte</th>
                <th className="text-left p-3 font-medium text-slate-600 whitespace-nowrap">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={12} className="p-8 text-center text-slate-400">
                    <RefreshCw className="w-6 h-6 mx-auto animate-spin mb-2" />
                    Chargement...
                  </td>
                </tr>
              ) : data?.items?.length === 0 ? (
                <tr>
                  <td colSpan={12} className="p-8 text-center text-slate-400">
                    <Database className="w-8 h-8 mx-auto opacity-40 mb-2" />
                    Aucune donnée pour ces filtres
                  </td>
                </tr>
              ) : (
                data?.items?.map((row, idx) => (
                  <tr key={idx} className="hover:bg-slate-50">
                    <td className="p-3 text-slate-600 whitespace-nowrap">
                      {row.observation_date ? new Date(row.observation_date).toLocaleDateString("fr-FR") : "—"}
                    </td>
                    <td className="p-3 font-medium text-slate-800 whitespace-nowrap">
                      {row.patient_name || "—"}
                    </td>
                    <td className="p-3 text-slate-600 whitespace-nowrap">
                      {row.clinic_name || "—"}
                    </td>
                    <td className="p-3 text-slate-600">
                      {row.cancer_type || "—"}
                      {row.cancer_detail && <span className="text-xs text-slate-400 ml-1">({row.cancer_detail})</span>}
                    </td>
                    <td className="p-3 text-slate-600">
                      {row.treatment_type || "—"}
                      {row.protocol && <div className="text-xs text-blue-600">{row.protocol}</div>}
                    </td>
                    <td className="p-3">{formatScore(row.fatigue)}</td>
                    <td className="p-3">{formatScore(row.douleur)}</td>
                    <td className="p-3">{formatScore(row.nausee_vomissements)}</td>
                    <td className="p-3 text-slate-600">{row.temperature || "—"}</td>
                    <td className="p-3 text-slate-600 whitespace-nowrap">
                      {row.tension_systolique && row.tension_diastolique 
                        ? `${row.tension_systolique}/${row.tension_diastolique}` 
                        : "—"}
                    </td>
                    <td className="p-3">
                      <AlertBadge level={row.alert_level} triggered={row.alert_triggered} />
                    </td>
                    <td className="p-3 text-xs text-slate-500 max-w-[150px] truncate">
                      {row.medical_decision || row.nurse_action || "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {data && data.total_pages > 1 && (
          <div className="flex items-center justify-between p-4 border-t bg-slate-50">
            <span className="text-sm text-slate-500">
              Page {data.page} sur {data.total_pages} ({data.total} résultats)
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg border border-slate-200 hover:bg-white disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage(p => Math.min(data.total_pages, p + 1))}
                disabled={page === data.total_pages}
                className="p-2 rounded-lg border border-slate-200 hover:bg-white disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
